import {
  getCorporateDetail,
  getCorporateLocations,
  getNearbyStudios,
} from '../../../api/corporateApi'
import CSVExporter from '../../../components/common/csv-exporter'
import TencentMap from '../../../components/tencent-map/tencent-map'
import translate from '../../../i18n'
import { ICorporateLocation, INearbyStudio } from '../share-type'
import styles from './nearby-studios.module.less'
import { SearchOutlined } from '@ant-design/icons'
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  InputNumber,
  notification,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

interface NearbyStudiosFormValues {
  locationId: number
  distanceKm: number
}

interface StudioFilterValues {
  studioId?: number | null
  studioName?: string
}

const DEFAULT_DISTANCE_KM = 3
const DEFAULT_PAGE_SIZE = 20

const NearbyStudios = () => {
  const { id } = useParams()
  const corporateId = Number(id)
  const [searchParams] = useSearchParams()
  const [form] = Form.useForm<NearbyStudiosFormValues>()
  const [studioFilterForm] = Form.useForm<StudioFilterValues>()
  const [corporateName, setCorporateName] = useState('')
  const [locations, setLocations] = useState<ICorporateLocation[]>([])
  const [selectedLocation, setSelectedLocation] = useState<ICorporateLocation>()
  const [distanceKm, setDistanceKm] = useState(DEFAULT_DISTANCE_KM)
  const [studios, setStudios] = useState<INearbyStudio[]>([])
  const [selectedStudioId, setSelectedStudioId] = useState<number>()
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [loading, setLoading] = useState(false)
  const [initializing, setInitializing] = useState(true)
  const [studioFilters, setStudioFilters] = useState<StudioFilterValues>({})

  const queryNearbyStudios = useCallback(
    (locationId: number, distance: number, availableLocations: ICorporateLocation[]) => {
      setLoading(true)
      setSelectedStudioId(undefined)
      return getNearbyStudios(corporateId, locationId, distance)
        .then(response => {
          setSelectedLocation(response.corporateLocation)
          setDistanceKm(response.distanceKm)
          setStudios(response.studios)
          setStudioFilters({})
          studioFilterForm.resetFields()
          setCurrentPage(1)
        })
        .catch(error => {
          setStudios([])
          setSelectedLocation(availableLocations.find(location => location.id === locationId))
          notification.error({
            message: translate('corporate.nearbyStudios.loadFailed'),
            description: error?.message,
          })
        })
        .finally(() => setLoading(false))
    },
    [corporateId, studioFilterForm],
  )

  useEffect(() => {
    Promise.all([getCorporateDetail(corporateId), getCorporateLocations(corporateId)])
      .then(([corporate, corporateLocations]) => {
        setCorporateName(corporate.corporate.name)
        setLocations(corporateLocations)
        const requestedLocationId = Number(searchParams.get('locationId'))
        const initialLocation =
          corporateLocations.find(location => location.id === requestedLocationId) ||
          corporateLocations[0]
        if (initialLocation) {
          form.setFieldsValue({
            locationId: initialLocation.id,
            distanceKm: DEFAULT_DISTANCE_KM,
          })
          return queryNearbyStudios(initialLocation.id, DEFAULT_DISTANCE_KM, corporateLocations)
        }
      })
      .catch(error => {
        notification.error({
          message: translate('corporate.location.loadFailed'),
          description: error?.message,
        })
      })
      .finally(() => setInitializing(false))
  }, [corporateId, form, queryNearbyStudios, searchParams])

  const locationOptions = useMemo(
    () =>
      locations.map(location => ({
        value: location.id,
        label: [location.cityName, location.businessDistrictName, location.address]
          .filter(Boolean)
          .join(' / '),
      })),
    [locations],
  )

  const handleSearch = (values: NearbyStudiosFormValues) =>
    queryNearbyStudios(values.locationId, values.distanceKm, locations)

  const filteredStudios = useMemo(() => {
    const normalizedName = studioFilters.studioName?.trim().toLowerCase()
    return studios.filter(studio => {
      const matchesId =
        studioFilters.studioId === undefined ||
        studioFilters.studioId === null ||
        studio.id === studioFilters.studioId
      const matchesName = !normalizedName || studio.name.toLowerCase().includes(normalizedName)
      return matchesId && matchesName
    })
  }, [studioFilters, studios])

  const handleStudioSelect = useCallback(
    (studioId: number) => {
      setSelectedStudioId(studioId)
      const index = filteredStudios.findIndex(studio => studio.id === studioId)
      if (index >= 0) {
        setCurrentPage(Math.floor(index / pageSize) + 1)
      }
    },
    [filteredStudios, pageSize],
  )

  const applyStudioFilters = (values: StudioFilterValues) => {
    setStudioFilters(values)
    setSelectedStudioId(undefined)
    setCurrentPage(1)
  }

  const columns: ColumnsType<INearbyStudio> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: value => (
        <Link to={`/studios/${value}`} onClick={event => event.stopPropagation()}>
          {value}
        </Link>
      ),
    },
    {
      title: translate('corporate.nearbyStudios.studioName'),
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: { showTitle: false },
      render: value => (
        <Tooltip title={value} placement="topLeft">
          <span className={styles['single-line-cell']}>{value}</span>
        </Tooltip>
      ),
    },
    {
      title: translate('corporate.location.businessDistrict'),
      dataIndex: 'businessDistrictName',
      key: 'businessDistrictName',
      width: 90,
      render: value => value || '-',
    },
    {
      title: translate('corporate.location.address'),
      dataIndex: 'address',
      key: 'address',
      width: 200,
      ellipsis: { showTitle: false },
      render: value => (
        <Tooltip title={value || '-'} placement="topLeft">
          <span className={styles['single-line-cell']}>{value || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: translate('corporate.nearbyStudios.distanceValue'),
      dataIndex: 'distanceKm',
      key: 'distanceKm',
      width: 100,
      render: value => Number(value).toFixed(2),
    },
  ]

  const exportData = filteredStudios.map(studio => ({
    企业名称: corporateName,
    企业位置: selectedLocation?.address || '',
    '距离范围（公里）': distanceKm,
    场馆ID: studio.id,
    场馆名称: studio.name,
    城市: studio.cityName || '',
    商圈: studio.businessDistrictName || '',
    场馆地址: studio.address || '',
    经度: studio.longitude,
    纬度: studio.latitude,
    '距离（公里）': studio.distanceKm.toFixed(2),
  }))

  if (!initializing && locations.length === 0) {
    return <Alert type="info" showIcon message={translate('corporate.location.title')} />
  }

  return (
    <div className={styles.page}>
      <div className={styles['page-heading']}>
        <Typography.Text type="secondary">
          {translate('corporate.nearbyStudios.corporate')}
        </Typography.Text>
        <Typography.Text strong className={styles['corporate-name']}>
          {corporateName || '-'}
        </Typography.Text>
      </div>
      <Form className={styles.filters} form={form} layout="inline" onFinish={handleSearch}>
        <Form.Item
          name="locationId"
          label={translate('corporate.nearbyStudios.location')}
          rules={[{ required: true }]}
        >
          <Select style={{ minWidth: 360 }} options={locationOptions} />
        </Form.Item>
        <Form.Item
          name="distanceKm"
          label={translate('corporate.nearbyStudios.distance')}
          rules={[
            {
              required: true,
              type: 'number',
              min: 0.0000001,
              message: translate('corporate.nearbyStudios.distanceRequired'),
            },
          ]}
        >
          <InputNumber
            min={0.0000001}
            step={0.1}
            addonAfter={translate('corporate.nearbyStudios.distanceUnit')}
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} icon={<SearchOutlined />}>
            {translate('corporate.nearbyStudios.search')}
          </Button>
        </Form.Item>
        <Form.Item>
          <CSVExporter
            data={exportData}
            filename={`corporate-nearby-studios-${corporateId}-${selectedLocation?.id || ''}-${dayjs().format('YYYYMMDDHHmmss')}.csv`}
          >
            {translate('corporate.nearbyStudios.export')}
          </CSVExporter>
        </Form.Item>
      </Form>

      <div className={styles.content}>
        <div className={styles.map}>
          {selectedLocation ? (
            <TencentMap
              location={selectedLocation}
              studios={studios}
              distanceKm={distanceKm}
              selectedStudioId={selectedStudioId}
              onStudioSelect={handleStudioSelect}
            />
          ) : (
            <Empty />
          )}
        </div>
        <div className={styles.list}>
          <Form
            className={styles['list-filters']}
            form={studioFilterForm}
            layout="inline"
            onFinish={applyStudioFilters}
          >
            <Form.Item name="studioId" label="ID">
              <InputNumber min={1} precision={0} style={{ width: 100 }} />
            </Form.Item>
            <Form.Item name="studioName" label={translate('corporate.nearbyStudios.studioName')}>
              <Input allowClear style={{ width: 150 }} />
            </Form.Item>
            <Form.Item>
              <Space size={4}>
                <Button htmlType="submit" icon={<SearchOutlined />}>
                  {translate('corporate.nearbyStudios.search')}
                </Button>
                <Button
                  onClick={() => {
                    studioFilterForm.resetFields()
                    applyStudioFilters({})
                  }}
                >
                  {translate('reset')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
          <Table
            rowKey="id"
            size="small"
            tableLayout="fixed"
            loading={loading || initializing}
            columns={columns}
            dataSource={filteredStudios}
            locale={{ emptyText: translate('corporate.nearbyStudios.empty') }}
            rowClassName={record => (record.id === selectedStudioId ? styles['selected-row'] : '')}
            onRow={record => ({ onClick: () => handleStudioSelect(record.id) })}
            pagination={{
              current: currentPage,
              pageSize,
              total: filteredStudios.length,
              size: 'small',
              showSizeChanger: true,
              onChange: (page, size) => {
                setCurrentPage(page)
                setPageSize(size)
              },
            }}
            scroll={{ y: 'calc(100vh - 480px)' }}
          />
        </div>
      </div>
    </div>
  )
}

export default NearbyStudios
